import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { find, sumBy } from 'lodash'
import { db } from '../../../../config/firebase'
import styles from './OrderManager.module.scss'
import Spinner from '../../../../ui/Spinner/Spinner'
import ModulePanel from '../../../../ui/ModulePanel/ModulePanel'
import InfoLine from '../../../../ui/InfoLine/InfoLine'
import DataTable from '../../../../ui/DataTable/DataTable'
import Price from '../../../Products/components/ProductsList/Price'
import { formatDateAsDayMontYearHHMM } from '../../../../utils/date/dateFormat'
import OrderStatus, { ORDER_STATUS_CONFIG } from '../OrderStatus/OrderStatus'
import OrderDeliveryInfo from '../OrderDeliveryInfo/OrderDeliveryInfo'
import OrderPaymentInfo from '../OrderPaymentInfo/OrderPaymentInfo'
import getDoc from '../../../../utils/db/getDoc'
import getCollection from '../../../../utils/db/getCollection'
import OrderProductCell from './OrderProductCell'
import isOrderProductOutlet from './isOrderProductOutlet'
import buildOrderCatalogItem from '../../utils/buildOrderCatalogItem'
import { OrderCatalogBadges, OrderProductCatalogBadge } from '../OrderCatalogBadges/OrderCatalogBadges'
import Button from '../../../../ui/Button/Button'
import PopUp from '../../../../ui/PopUp/PopUp'
import FormRender from '../../../../utils/newforms/render/FormRender'
import { createForm } from '../../../../utils/newforms/createForm'
import getFieldRenderObject from '../../../../utils/newforms/render/getFieldRenderObject'
import isFormValid from '../../../../utils/newforms/validation/isFormValid'
import getFormValues from '../../../../utils/newforms/getFormValues'
import { updateDoc } from '../../../../utils/db/updateDoc'
import StatusMessage, { useStatusMessage } from '../../../../ui/StatusMessage/StatusMessage'
import { GIFT_FUND_ITEMS_COLLECTION } from '../../../../api/marketing/gifts/giftFundConstants'
import {
  getCatalogProductImageUrl,
  getFundItemAvatarImageUrl,
  getProductAvatarFileName,
} from '../../../../api/marketing/gifts/giftFundHelpers'

function OrderManager({ ...router }) {
  const { orderId } = router.match.params
  const [order, setOrder] = useState(null)
  const [productRows, setProductRows] = useState([])
  const [popupState, setPopupState] = useState(null)

  useEffect(() => {
    if (!orderId) return undefined

    const unsubscribe = db
      .collection('orders')
      .doc(orderId)
      .onSnapshot((doc) => {
        if (!doc.exists) {
          setOrder(null)
          return
        }

        setOrder({
          id: doc.id,
          ...doc.data(),
        })
      })

    return () => unsubscribe()
  }, [orderId])

  useEffect(() => {
    const enrichProducts = async () => {
      if (!order || !Array.isArray(order.products) || !order.products.length) {
        setProductRows([])
        return
      }

      const brands = await getCollection({ path: 'brands', docIdName: 'id' })

      const [productsData, outletProductsData, giftCatalogData, giftFundData] = await Promise.all([
        Promise.all(
          order.products.map((orderProduct) => {
            if (orderProduct?.isGift) return Promise.resolve(null)
            return getDoc({
              path: 'products',
              docId: orderProduct.catalogProductId || orderProduct.id,
              docIdName: 'id',
            })
          })
        ),
        Promise.all(
          order.products.map((orderProduct) => {
            if (orderProduct?.isGift) return Promise.resolve(null)
            return getDoc({
              path: 'outletProducts',
              docId: orderProduct.id,
              docIdName: 'id',
            })
          })
        ),
        Promise.all(
          order.products.map((orderProduct) => {
            if (!orderProduct?.isGift) return Promise.resolve(null)
            const catalogProductId = String(orderProduct.catalogProductId || '').trim()
            if (!catalogProductId) return Promise.resolve(null)
            return getDoc({
              path: 'products',
              docId: catalogProductId,
              docIdName: 'id',
            })
          })
        ),
        Promise.all(
          order.products.map((orderProduct) => {
            if (!orderProduct?.isGift) return Promise.resolve(null)
            const fundItemId = String(orderProduct.fundItemId || '').trim()
            if (!fundItemId) return Promise.resolve(null)
            return getDoc({
              path: GIFT_FUND_ITEMS_COLLECTION,
              docId: fundItemId,
              docIdName: 'id',
            })
          })
        ),
      ])

      const rows = order.products.map((orderProduct, index) => {
        if (orderProduct?.isGift) {
          const giftProduct = giftCatalogData[index] || null
          const fundItem = giftFundData[index] || null
          const catalogProductId =
            String(orderProduct.catalogProductId || giftProduct?.id || '').trim() || null
          const catalogSource = fundItem?.catalogSource === 'outlet' ? 'outlet' : 'premium'

          let avatarSrc = fundItem ? getFundItemAvatarImageUrl(fundItem) : null
          if (!avatarSrc && giftProduct) {
            const fileName = getProductAvatarFileName(giftProduct)
            avatarSrc = getCatalogProductImageUrl({
              productId: giftProduct.id,
              fileName,
              catalogSource,
            })
          }

          return {
            id: `gift-${orderProduct.id || index}`,
            productId: catalogProductId || orderProduct.fundItemId || orderProduct.id,
            slug: typeof giftProduct?.slug === 'string' ? giftProduct.slug : null,
            brandId: giftProduct?.brandId || null,
            name: orderProduct.title || giftProduct?.name || 'Подарунок',
            descriptionShort: [orderProduct.volumeText, orderProduct.sourceType]
              .filter(Boolean)
              .join(' · '),
            barcode: giftProduct?.barcode || '-',
            brand: orderProduct.brandName || '-',
            avatar: null,
            avatarSrc,
            price: 0,
            quantity: orderProduct.quantity || 1,
            isGift: true,
            isOutlet: false,
            outletReason: null,
            expirationOption: null,
            missingExpirationOption: false,
            outletOptionId: null,
          }
        }

        const premiumData = find(productsData, { id: orderProduct.id })
        const outletData = find(outletProductsData, { id: orderProduct.id })
        const isOutlet = isOrderProductOutlet(orderProduct, outletData)
        const catalogData = isOutlet ? outletData : premiumData
        const brandData = catalogData ? find(brands, { id: catalogData.brandId }) : null
        const catalogItem = buildOrderCatalogItem(orderProduct, outletData)
        const slug =
          (typeof premiumData?.slug === 'string' && premiumData.slug) ||
          (typeof catalogData?.slug === 'string' && catalogData.slug) ||
          null

        return {
          id: orderProduct.id,
          productId: orderProduct.id,
          slug,
          brandId: catalogData?.brandId || null,
          name: catalogData?.name || '-',
          descriptionShort: catalogData?.descriptionShort || '',
          barcode: catalogData?.barcode || '-',
          brand: brandData?.name || '-',
          avatar: find(catalogData?.photos || [], ['isAvatar', true]) || null,
          avatarSrc: null,
          price: orderProduct.price || 0,
          priceBeforePromo: orderProduct.priceBeforePromo || null,
          lineDiscount: Number(orderProduct.lineDiscount) || 0,
          lineSubtotalAfter:
            orderProduct.lineSubtotalAfter != null
              ? Number(orderProduct.lineSubtotalAfter)
              : null,
          quantity: orderProduct.quantity || 0,
          isGift: false,
          promoEligible: orderProduct.promoEligible === true,
          ...catalogItem,
        }
      })

      setProductRows(rows)
    }

    enrichProducts()
  }, [order])

  const deliveryFieldName = order?.deilivery ? 'deilivery' : 'delivery'

  if (!order) {
    return (
      <div className={styles.root}>
        <Spinner type="module" />
      </div>
    )
  }

  const products = Array.isArray(order.products) ? order.products : []
  const paidProducts = products.filter((product) => !product?.isGift)
  const giftProducts = products.filter((product) => product?.isGift)
  const paidRows = productRows.filter((row) => !row.isGift)
  const giftRows = productRows.filter((row) => row.isGift)
  const promo = order.promo && order.promo.codeNormalized ? order.promo : null

  const lineTotalAfter = (product) => {
    if (product?.lineSubtotalAfter != null && Number.isFinite(Number(product.lineSubtotalAfter))) {
      return Number(product.lineSubtotalAfter)
    }
    return (product.quantity || 0) * (product.price || 0)
  }

  const lineTotalBefore = (product) => {
    if (product?.lineSubtotalBefore != null && Number.isFinite(Number(product.lineSubtotalBefore))) {
      return Number(product.lineSubtotalBefore)
    }
    const unitBefore =
      product?.priceBeforePromo != null && Number.isFinite(Number(product.priceBeforePromo))
        ? Number(product.priceBeforePromo)
        : Number(product.price) || 0
    return (product.quantity || 0) * unitBefore
  }

  const unitPriceBefore = (row) => {
    if (row?.priceBeforePromo != null && Number.isFinite(Number(row.priceBeforePromo))) {
      return Number(row.priceBeforePromo)
    }
    return Number(row.price) || 0
  }

  const paidTotalAfter = sumBy(paidProducts, lineTotalAfter)
  const paidTotalBefore =
    promo != null && promo.productsSubtotalBefore != null && Number.isFinite(Number(promo.productsSubtotalBefore))
      ? Number(promo.productsSubtotalBefore)
      : sumBy(paidProducts, lineTotalBefore)
  const promoDiscountTotal =
    promo != null
      ? Number(promo.discountTotal) || sumBy(paidProducts, (p) => Number(p.lineDiscount) || 0)
      : 0
  const outletCount = paidRows.filter((row) => row.isOutlet).length

  const productColumns = [
    {
      field: 'productId',
      headerClassName: 'TableHeadCol',
      headerName: 'ID',
      width: 220,
    },
    {
      field: 'productType',
      headerClassName: 'TableHeadCol',
      headerName: 'Каталог',
      width: 100,
      renderCell: (params) => <OrderProductCatalogBadge {...params.row} />,
    },
    {
      field: 'product',
      headerClassName: 'TableHeadCol',
      headerName: 'Товар',
      minWidth: 320,
      flex: 1,
      renderCell: (params) => <OrderProductCell row={params.row} />,
    },
    {
      field: 'brand',
      headerClassName: 'TableHeadCol',
      headerName: 'Бренд',
      width: 180,
    },
    {
      field: 'barcode',
      headerClassName: 'TableHeadCol',
      headerName: 'Штрихкод',
      width: 180,
    },
    {
      field: 'price',
      headerClassName: 'TableHeadCol',
      headerName: 'Ціна',
      width: 120,
      renderCell: (params) => <Price num={unitPriceBefore(params.row)} />,
    },
    {
      field: 'promoDiscount',
      headerClassName: 'TableHeadCol',
      headerName: 'Промо',
      width: 110,
      renderCell: (params) => {
        const discount = Number(params.row.lineDiscount) || 0
        if (!(discount > 0)) return '—'
        return (
          <span className={styles.promoDiscountValue}>
            −
            <Price num={discount} />
          </span>
        )
      },
    },
    {
      field: 'quantity',
      headerClassName: 'TableHeadCol',
      headerName: 'К-сть',
      width: 100,
      renderCell: (params) =>
        params.row.quantity > 1 ? (
          <span className={styles.highlightedValue}>{params.row.quantity}</span>
        ) : (
          params.row.quantity
        ),
    },
    {
      field: 'total',
      headerClassName: 'TableHeadCol',
      headerName: 'Сума',
      width: 140,
      renderCell: (params) => {
        const row = params.row
        const total =
          row.lineSubtotalAfter != null && Number.isFinite(Number(row.lineSubtotalAfter))
            ? Number(row.lineSubtotalAfter)
            : (row.quantity || 0) * (Number(row.price) || 0)
        return <Price num={total} />
      },
    },
  ]

  return (
    <div className={styles.root}>
      <ModulePanel title={`Замовлення ${order.orderNum || ''}`}>
        <div className={styles.infoGrid}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Основна інформація</div>
            <InfoLine title="Номер" content={order.orderNum || '-'} />
            <InfoLine title="id" content={order.id} />
            <InfoLine title="Дата" content={order.created ? formatDateAsDayMontYearHHMM(order.created) : '-'} />
            <InfoLine title="Статус" content={<OrderStatus status={order.status} />} />
            {outletCount > 0 && (
              <InfoLine title="Каталог" content={<OrderCatalogBadges products={paidProducts} />} />
            )}
            {outletCount > 0 && (
              <InfoLine title="Outlet" content={`${outletCount} поз. з ${paidProducts.length}`} />
            )}
            <div className={styles.sectionActions}>
              <Button title="Змінити статус" theme="solid" fill="accent" onClick={() => setPopupState('status')} />
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Клієнт</div>
            <InfoLine title="Ім'я" content={order.guest?.name || '-'} />
            <InfoLine title="Прізвище" content={order.guest?.lastName || '-'} />
            <InfoLine title="Телефон" content={order.guest?.phone || '-'} />
            <InfoLine title="E-mail" content={order.guest?.email || '-'} />
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Доставка</div>
            <OrderDeliveryInfo {...(order.deilivery || order.delivery || {})} />
            <div className={styles.sectionActions}>
              <Button title="Вказати № ТТН" theme="solid" fill="accent" onClick={() => setPopupState('ttn')} />
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Оплата</div>
            <OrderPaymentInfo type={order.payment?.type} />
            <div className={styles.totalBlock}>
              <InfoLine title="Позицій" content={paidProducts.length} />
              <InfoLine title="Одиниць" content={sumBy(paidProducts, (product) => product.quantity || 0)} />
              {giftProducts.length > 0 ? (
                <InfoLine title="Подарунків" content={giftProducts.length} />
              ) : order.giftWithPurchase?.status === 'declined' ? (
                <InfoLine title="Подарунків" content="Без подарунків" />
              ) : null}
              {promo ? (
                <div className={styles.promoTicket}>
                  <div className={styles.promoTicketHead}>Промокод</div>
                  <InfoLine
                    title="Код"
                    content={<span className={styles.promoCodeValue}>{promo.codeNormalized}</span>}
                  />
                  {promo.title ? <InfoLine title="Акція" content={promo.title} /> : null}
                  {promo.scopeLabel ? (
                    <InfoLine title="Асортимент" content={promo.scopeLabel} />
                  ) : null}
                </div>
              ) : null}
              {promo ? (
                <>
                  <InfoLine title="Сума без промокоду" content={<Price num={paidTotalBefore} />} />
                  {promoDiscountTotal > 0 ? (
                    <InfoLine
                      title="Знижка"
                      content={
                        <span className={styles.promoDiscountValue}>
                          −
                          <Price num={promoDiscountTotal} />
                        </span>
                      }
                    />
                  ) : null}
                  <InfoLine title="До сплати" content={<Price num={paidTotalAfter} />} />
                </>
              ) : (
                <InfoLine title="Сума" content={<Price num={paidTotalAfter} />} />
              )}
            </div>
          </div>
        </div>
      </ModulePanel>

      <ModulePanel title="Товари у замовленні">
        <DataTable
          initialState={{
            sorting: {
              sortModel: [{ field: 'name', sort: 'asc' }],
            },
          }}
          table={{
            rows: paidRows,
            columns: productColumns,
            autoHeight: true,
            disableRowSelectionOnClick: true,
          }}
        />
      </ModulePanel>

      {giftRows.length > 0 ? (
        <ModulePanel title="Подарунки у замовленні">
          <DataTable
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            table={{
              rows: giftRows,
              columns: productColumns,
              autoHeight: true,
              disableRowSelectionOnClick: true,
            }}
          />
        </ModulePanel>
      ) : order.giftWithPurchase?.status === 'declined' ? (
        <ModulePanel title="Подарунки у замовленні">
          <p className={styles.giftDeclinedNote}>Без подарунків — клієнт відмовився.</p>
        </ModulePanel>
      ) : null}

      {popupState === 'status' && (
        <OrderStatusPopUp orderId={order.id} currentStatus={order.status} onClose={() => setPopupState(null)} />
      )}

      {popupState === 'ttn' && (
        <OrderTtnPopUp
          orderId={order.id}
          deliveryFieldName={deliveryFieldName}
          currentTtn={order[deliveryFieldName]?.ttn || ''}
          onClose={() => setPopupState(null)}
        />
      )}
    </div>
  )
}

function OrderStatusPopUp({ orderId, currentStatus, onClose }) {
  const [form, setForm] = useState(
    createForm({
      formPattern: new OrderStatusForm(),
      formData: { status: currentStatus },
    })
  )
  const [showErrors] = useState(false)
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async () => {
    if (!isFormValid({ form })) {
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Заповніть поле статусу',
        closeAfter: 5000,
      })
    }

    setIsLoading(true)
    const data = getFormValues({ form })
    await updateDoc({ path: 'orders', docId: orderId, data: { status: data.status } })
    onClose()
  }

  return (
    <PopUp
      title="Змінити статус"
      customButtons={[
        <Button key="save" isLoading={isLoading} onClick={onSubmit} title="Зберегти" theme="solid" fill="accent" />,
        <Button key="close" onClick={onClose} title="Закрити" theme="bounded" border="gray" color="gray" />,
      ]}
      show
      close={onClose}
    >
      <div className={styles.popUpContent}>
        {statusMessage.show && (
          <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
        )}
        <FormRender sections={[{ fields: ['status'] }]} form={form} setForm={setForm} errors={showErrors} />
      </div>
    </PopUp>
  )
}

function OrderTtnPopUp({ orderId, deliveryFieldName, currentTtn, onClose }) {
  const [form, setForm] = useState(
    createForm({
      formPattern: new OrderTtnForm(),
      formData: { ttn: currentTtn },
    })
  )
  const [showErrors] = useState(false)
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async () => {
    setIsLoading(true)
    const data = getFormValues({ form })
    await updateDoc({
      path: 'orders',
      docId: orderId,
      data: {
        [`${deliveryFieldName}.ttn`]: data.ttn || '',
      },
    })
    onClose()
  }

  return (
    <PopUp
      title="Вказати № ТТН"
      customButtons={[
        <Button key="save" isLoading={isLoading} onClick={onSubmit} title="Зберегти" theme="solid" fill="accent" />,
        <Button key="close" onClick={onClose} title="Закрити" theme="bounded" border="gray" color="gray" />,
      ]}
      show
      close={onClose}
    >
      <div className={styles.popUpContent}>
        {statusMessage.show && (
          <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
        )}
        <FormRender sections={[{ fields: ['ttn'] }]} form={form} setForm={setForm} errors={showErrors} />
      </div>
    </PopUp>
  )
}

class OrderStatusForm {
  constructor() {
    this.status = {
      field: {
        fieldId: 'status',
        fieldType: 'select',
        getOptions: Object.entries(ORDER_STATUS_CONFIG).map(([value, config]) => ({
          value,
          label: config.label,
        })),
        label: 'Статус',
        required: true,
      },
      render: getFieldRenderObject(),
    }
  }
}

class OrderTtnForm {
  constructor() {
    this.ttn = {
      field: {
        fieldId: 'ttn',
        fieldType: 'input',
        inputType: 'text',
        label: '№ ТТН',
        required: false,
      },
      render: getFieldRenderObject(),
    }
  }
}

export default withRouter(OrderManager)
